import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { apiGet, apiPost, ApiError } from "../lib/api";

export type DeskUser = {
  id: string;
  email: string;
  display_name: string;
};

type AuthStatus = {
  auth_required: boolean;
  user_count: number;
  registration_open: boolean;
  database: boolean;
};

type AuthContextValue = {
  user: DeskUser | null;
  loading: boolean;
  authRequired: boolean;
  registrationOpen: boolean;
  refresh: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, displayName?: string) => Promise<void>;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<DeskUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [authRequired, setAuthRequired] = useState(false);
  const [registrationOpen, setRegistrationOpen] = useState(false);

  const refresh = useCallback(async () => {
    const status = await apiGet<AuthStatus & { ok: boolean }>("/api/auth/status", {
      timeoutMs: 15_000,
    });
    setAuthRequired(Boolean(status.auth_required));
    setRegistrationOpen(Boolean(status.registration_open));
    if (!status.auth_required) {
      setUser(null);
      return;
    }
    try {
      const me = await apiGet<{ ok: boolean; user: DeskUser }>("/api/auth/me", {
        timeoutMs: 15_000,
      });
      setUser(me.user);
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        setUser(null);
        return;
      }
      throw e;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await refresh();
      } catch {
        if (!cancelled) {
          setAuthRequired(false);
          setUser(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  const login = useCallback(
    async (email: string, password: string) => {
      const res = await apiPost<{ ok: boolean; user: DeskUser }>("/api/auth/login", {
        email,
        password,
      });
      setUser(res.user);
      setAuthRequired(true);
    },
    [],
  );

  const register = useCallback(
    async (email: string, password: string, displayName = "") => {
      const res = await apiPost<{ ok: boolean; user: DeskUser }>("/api/auth/register", {
        email,
        password,
        display_name: displayName,
      });
      setUser(res.user);
      setAuthRequired(true);
    },
    [],
  );

  const logout = useCallback(async () => {
    await apiPost("/api/auth/logout");
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      authRequired,
      registrationOpen,
      refresh,
      login,
      register,
      logout,
    }),
    [user, loading, authRequired, registrationOpen, refresh, login, register, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
